import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Edit3, User } from "lucide-react";
import { toast } from "sonner";

const empty = () => ({ name: "", email: "", phone: "", fiscal_code: "", notes: "" });

export default function Proprietari() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);

  const load = async () => { const { data } = await http.get("/owners"); setItems(data); };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("new") === "1") { setForm(empty()); setEditId(null); setOpen(true); params.delete("new"); setParams(params, { replace: true }); } }, [params, setParams]);

  const onSubmit = async (e) => { e.preventDefault();
    try { if (editId) await http.put(`/owners/${editId}`, form); else await http.post("/owners", form); toast.success("Salvato"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onDelete = async (id) => { if (!window.confirm("Eliminare?")) return; await http.delete(`/owners/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="proprietari-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Anagrafica</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Proprietari</h1>
        </div>
        <Button onClick={() => { setForm(empty()); setEditId(null); setOpen(true); }} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl" data-testid="new-owner-btn"><Plus className="h-4 w-4 mr-1" /> Proprietario</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="col-span-full text-center text-slate-400 py-10">Nessun proprietario ancora.</div>}
        {items.map((o) => (
          <Card key={o.id} className="rounded-2xl p-5">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-terracotta-light flex items-center justify-center" style={{ background: "rgba(224,122,95,0.15)" }}><User className="h-5 w-5 text-[var(--terracotta)]" /></div>
                <div><div className="font-display text-lg font-bold">{o.name}</div><div className="text-xs text-slate-500">{o.email || "-"}</div></div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setForm({...empty(), ...o}); setEditId(o.id); setOpen(true); }} className="p-1.5 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
                <button onClick={() => onDelete(o.id)} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-3 text-sm space-y-1 text-slate-600">
              {o.phone && <div>📞 {o.phone}</div>}
              {o.fiscal_code && <div className="font-mono text-xs">CF: {o.fiscal_code}</div>}
              {o.notes && <div className="text-xs text-slate-500 line-clamp-2">{o.notes}</div>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Modifica" : "Nuovo"} proprietario</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div><Label>Nome completo</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="owner-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label>Codice Fiscale / P.IVA</Label><Input value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} /></div>
            <div><Label>Note</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="owner-submit">Salva</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
